import React, { useState } from 'react';
import { useCompany } from '@/context/CompanyContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { UserPlus, Loader2, Mail, Crown } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

export const TeamInvitation: React.FC = () => {
  const { inviteMember, userRole, invitations, members } = useCompany();
  const { hasAccess } = useSubscription();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'vendeur'>('vendeur');
  const [isInviting, setIsInviting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Only admins can invite members
  if (userRole !== 'admin') {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    // Check subscription access for invitations
    if (!hasAccess) {
      toast({
        title: "Fonctionnalité Premium requise",
        description: "L'invitation de membres d'équipe nécessite un abonnement Starter.",
        variant: "destructive",
      });
      return;
    }

    // Avec l'abonnement Starter : limité à 4 membres d'équipe (en plus de l'admin)
    const totalMembers = (members?.length || 0);
    if (totalMembers >= 5) {
      toast({
        title: "Limite d'équipe atteinte",
        description: "L'abonnement Starter permet 4 membres d'équipe en plus de l'administrateur.",
        variant: "destructive",
      });
      return;
    }

    setIsInviting(true);
    const success = await inviteMember(email.trim(), role);
    setIsInviting(false);

    if (success) {
      setEmail('');
      setRole('vendeur');
      setIsDialogOpen(false);
    }
  };

  const canInviteMore = (members?.length || 0) < 5;

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button disabled={!hasAccess || !canInviteMore}>
          <UserPlus className="w-4 h-4 mr-2" />
          {!canInviteMore ? "Limite atteinte (4 membres max)" : "Inviter un membre"}
          {!hasAccess && <Crown className="w-4 h-4 ml-2" />}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Inviter un nouveau membre
          </DialogTitle>
        </DialogHeader>
        <Card className="border-0 shadow-none">
          <CardContent className="p-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Adresse e-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="exemple@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isInviting}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="role">Rôle</Label>
                <Select value={role} onValueChange={(value: 'member' | 'vendeur') => setRole(value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Membre</SelectItem>
                    <SelectItem value="vendeur">Vendeur</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1"
                  disabled={isInviting}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={!email.trim() || isInviting}
                >
                  {isInviting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer l'invitation
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};